import React from 'react';
import { motion } from 'framer-motion';
import { CONSTANTS } from '../data.js';
import { iconFor } from '../lib/utils';
import type { Hint } from '../types';

type HintPopupProps = {
  hint: Hint;
  onClose: () => void;
};

const RecipeIngredient: React.FC<{ elementKey: string | null }> = ({ elementKey }) => {
  const isKnown = elementKey && CONSTANTS.elements[elementKey];
  const name = isKnown ? CONSTANTS.elements[elementKey].name : '???';
  const icon = isKnown ? iconFor(elementKey) : iconFor('default');

  return (
    <div className="flex flex-col items-center text-center w-20">
      <div
        className="w-16 h-16 rounded-lg flex items-center justify-center mb-1"
        style={{
          background: `linear-gradient(135deg, ${CONSTANTS.ui.theme.colors.surface}40 0%, ${CONSTANTS.ui.theme.colors.background}40 100%)`,
          border: `1px solid ${CONSTANTS.ui.theme.colors.border}40`,
        }}
      >
        <img src={icon} alt={name} className="w-12 h-12 object-contain" />
      </div>
      <span
        className="font-crimson text-xs leading-tight"
        style={{ color: CONSTANTS.ui.theme.colors.textSecondary }}
      >
        {name}
      </span>
    </div>
  );
};

export const HintPopup: React.FC<HintPopupProps> = ({ hint, onClose }) => {
  const resultElement = CONSTANTS.elements[hint.result];

  return (
    <motion.div
      className="absolute inset-0 flex items-center justify-center z-50"
      style={{ background: 'rgba(26, 22, 17, 0.85)' }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="relative flex flex-col items-center p-8 rounded-xl w-[420px] max-w-[90vw]"
        style={{
          background: `linear-gradient(135deg, ${CONSTANTS.ui.theme.colors.surface} 0%, ${CONSTANTS.ui.theme.colors.background} 100%)`,
          border: `2px solid ${CONSTANTS.ui.theme.colors.primary}`,
          boxShadow: `${CONSTANTS.ui.theme.effects.ornateBoxShadow}, 0 0 20px rgba(218, 165, 32, 0.3)`
        }}
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        transition={{ duration: 0.2 }}
      >
        <div className="absolute top-3 left-3 w-6 h-6 border-l-2 border-t-2 rounded-tl-lg" style={{ borderColor: CONSTANTS.ui.theme.colors.primary }} />
        <div className="absolute top-3 right-3 w-6 h-6 border-r-2 border-t-2 rounded-tr-lg" style={{ borderColor: CONSTANTS.ui.theme.colors.primary }} />
        <div className="absolute bottom-3 left-3 w-6 h-6 border-l-2 border-b-2 rounded-bl-lg" style={{ borderColor: CONSTANTS.ui.theme.colors.primary }} />
        <div className="absolute bottom-3 right-3 w-6 h-6 border-r-2 border-b-2 rounded-br-lg" style={{ borderColor: CONSTANTS.ui.theme.colors.primary }} />

        <h2 
          className="font-cinzel text-2xl font-bold mb-4"
          style={{ 
            color: CONSTANTS.ui.theme.colors.primary,
            textShadow: CONSTANTS.ui.theme.effects.textGlow
          }}
        >
          Hint
        </h2>

        <p className="font-crimson text-sm text-center mb-6" style={{ color: CONSTANTS.ui.theme.colors.textSecondary }}>
          Try combining these two elements...
        </p>

        <div className="flex items-center justify-center gap-2 mb-4">
          <RecipeIngredient elementKey={hint.a} />
          <span className="font-bold text-2xl mx-2" style={{ color: CONSTANTS.ui.theme.colors.primary }}>+</span>
          <RecipeIngredient elementKey={hint.b} />
        </div>

        <p className="font-crimson text-sm text-center mb-6" style={{ color: CONSTANTS.ui.theme.colors.textSecondary }}>
          ...to create something new!
        </p>

        <motion.button
          onClick={onClose}
          className="font-crimson mt-4 w-full px-8 py-3 rounded-lg text-lg font-semibold transition-all duration-200 hover:scale-105 active:scale-95"
          style={{
            background: `linear-gradient(135deg, ${CONSTANTS.ui.theme.colors.primary} 0%, ${CONSTANTS.ui.theme.colors.accent} 100%)`,
            color: CONSTANTS.ui.theme.colors.background,
            border: `1px solid ${CONSTANTS.ui.theme.colors.border}`,
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)'
          }}
        >
          Got it!
        </motion.button>
      </motion.div>
    </motion.div>
  );
};
